/**
 * useTrustDerivation — Trust entity-type derivation engine.
 *
 * Analogue of `useIndividualDerivation` for the Trust branch. The big
 * difference vs Individual: Trust sub-type is NOT auto-derived from age /
 * citizenship — the officer explicitly picks it from the TrustIdentitySection
 * dropdown (Revocable / Irrevocable / QIT / SelfSettledSNT). The derivation
 * hook therefore focuses on:
 *
 *   1. Collapsing the officer-selected sub-type → shared CustomerType ('Trust')
 *   2. Publishing derived flags (callClass, tinCodeDefault, consumerFlag,
 *      cddExempt, occupation, cifConvention, risk-review / manager-approval
 *      requirements) that the section + save pipeline both consume.
 *   3. Existing-CIF reload handling mirrors the Individual hook's shape so
 *      downstream consumers (save dispatch, supervisor-gate UI) treat both
 *      derivations the same way.
 *
 * Admin-configurable wave-3 follow-ups:
 *   - Move TRUST_SUB_TYPES into SQL catalog (TrustSubTypes table)
 *   - Honor an "existingCif.autoPromoteOnReload" toggle + supervisor gate
 *     equivalent to the Individual hook
 *
 * Per Bill's 2026-04-23 admin-configurable mandate: no sub-type rules are
 * hardcoded in the dialog — every behavioral difference (risk review,
 * manager approval, debit block, etc.) flows from the row record read out
 * of TRUST_SUB_TYPES. That list moves to SQL in a later wave.
 */

import { useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import type { CustomerType } from '@retail-ops/shared-types/enums';
import type { RaoPerson } from '../../../types';
import {
  TRUST_SUB_TYPES,
  TRUST_SUB_TYPE_MAP,
  trustSubTypeToCustomerType,
  TRUST_CDD_EXEMPT_REASON,
  TRUST_OCCUPATION_LABEL,
  TRUST_BRANCH_PREFIX_CIF_CONVENTION,
  type TrustSubType,
  type TrustSubTypeOption,
} from './trust-config';

// -----------------------------------------------------------------------------
// Public API
// -----------------------------------------------------------------------------

/** Derivation source — parity with useIndividualDerivation. */
export type TrustDerivationSource = 'baseline' | 'subType';

export interface DerivationResult {
  /** The shared-enum CustomerType for this trust (always 'Trust' today). */
  readonly customerType: CustomerType;
  /** Finer Trust sub-type selected by the officer (or 'RevocableTrust' baseline). */
  readonly trustSubType: TrustSubType;
  /** The full sub-type option record for quick flag probing. */
  readonly subTypeOption: TrustSubTypeOption;
  /** Which input drove the derivation. */
  readonly derivedFrom: TrustDerivationSource;
  /**
   * For existing-CIF reload: the sub-type value that Core Director had on
   * file before the derivation engine ran. Present only when the stored
   * value differs from the current selection. UI uses this for the
   * "Entity type updated from 'X' to 'Y'" note.
   */
  readonly promotedFrom?: string;
  /**
   * True when the derivation engine wants supervisor approval before the
   * CustMod can commit. For Trust, this fires when (a) a sub-type change
   * is detected on an existing CIF or (b) sub-type requires Manager role
   * and the current officer isn't one (Irrevocable).
   */
  readonly requiresSupervisorApproval?: boolean;
  /** True when this sub-type needs Risk Management review before opening. */
  readonly requiresRiskReview: boolean;
  /** True when grantor must be blocked from the signer list. */
  readonly grantorCannotSign: boolean;
  /** True when debit cards must be blocked on accounts for this sub-type. */
  readonly blockDebitCards: boolean;
}

export interface UseTrustDerivationOptions {
  readonly mode: 'new' | 'existing';
  /** Sub-type on the stored CIF (when mode === 'existing'). */
  readonly storedSubType?: string;
}

// -----------------------------------------------------------------------------
// Officer-role probe (mirror of the Individual hook's helper)
// -----------------------------------------------------------------------------

function isManagerOrAbove(auth: ReturnType<typeof useAuth>): boolean {
  const user: unknown = (auth as unknown as { user?: unknown })?.user;
  if (!user || typeof user !== 'object') return false;
  const u = user as Record<string, unknown>;
  if (u.isManager === true) return true;
  if (u.isSupervisor === true) return true;
  if (typeof u.role === 'string') {
    const r = u.role.toLowerCase();
    if (r.includes('manager') || r.includes('supervisor') || r.includes('admin')) return true;
  }
  const roles = u.roles;
  if (Array.isArray(roles)) {
    return (roles as unknown[]).some((r) => {
      if (typeof r !== 'string') return false;
      const rl = r.toLowerCase();
      return rl.includes('manager') || rl.includes('supervisor') || rl.includes('admin');
    });
  }
  return false;
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

/** Read the officer-selected sub-type off of `person.extra.trustSubType`. */
function readSelectedSubType(person: RaoPerson): TrustSubType | null {
  const raw = person.extra?.trustSubType;
  if (typeof raw !== 'string') return null;
  return TRUST_SUB_TYPE_MAP.has(raw as TrustSubType) ? (raw as TrustSubType) : null;
}

/** Baseline sub-type when nothing is selected yet (new dialog). */
const BASELINE_SUB_TYPE: TrustSubType = 'RevocableTrust';

// -----------------------------------------------------------------------------
// Hook
// -----------------------------------------------------------------------------

export function useTrustDerivation(
  person: RaoPerson,
  opts: UseTrustDerivationOptions,
): DerivationResult {
  const auth = useAuth();

  return useMemo<DerivationResult>(() => {
    const officerIsManager = isManagerOrAbove(auth);
    const selected = readSelectedSubType(person);
    const subType: TrustSubType = selected ?? BASELINE_SUB_TYPE;
    const derivedFrom: TrustDerivationSource = selected ? 'subType' : 'baseline';

    const subTypeOption =
      TRUST_SUB_TYPE_MAP.get(subType) ??
      // Defensive — TRUST_SUB_TYPES is non-empty; this branch is unreachable
      // in practice but keeps TS happy without a non-null assertion.
      TRUST_SUB_TYPES[0];

    const customerType = trustSubTypeToCustomerType(subType);

    // Existing-CIF reload — surface promotion metadata when the officer
    // switched sub-type vs what Core Director had on file.
    const storedDiffersFromSelected =
      opts.mode === 'existing' &&
      typeof opts.storedSubType === 'string' &&
      opts.storedSubType !== subType;

    const managerGate =
      subTypeOption.requiresManagerApproval && !officerIsManager;

    const requiresSupervisorApproval = managerGate || storedDiffersFromSelected;

    return {
      customerType,
      trustSubType: subType,
      subTypeOption,
      derivedFrom,
      requiresRiskReview: subTypeOption.requiresRiskReview,
      grantorCannotSign: subTypeOption.grantorCannotSign,
      blockDebitCards: subTypeOption.blockDebitCards,
      ...(storedDiffersFromSelected ? { promotedFrom: opts.storedSubType } : {}),
      ...(requiresSupervisorApproval ? { requiresSupervisorApproval: true } : {}),
    };
  }, [person.extra, opts.mode, opts.storedSubType, auth]);
}

// -----------------------------------------------------------------------------
// Derived-fields helper (consumed by the identity section + save dispatch)
// -----------------------------------------------------------------------------

/**
 * Compute the RaoPerson field patch that represents the derived defaults
 * for the given Trust sub-type. Officer edits to these fields (tinCode,
 * callClass) win over the defaults — the caller only applies this patch
 * when the sub-type SELECTION changes, not on every render.
 */
export function buildDerivedTrustPatch(
  subType: TrustSubType,
  priorExtra: Record<string, unknown> | undefined,
): Partial<RaoPerson> {
  const opt = TRUST_SUB_TYPE_MAP.get(subType);
  if (!opt) return {};
  return {
    customerType: trustSubTypeToCustomerType(subType),
    callClass: opt.cifCallClass,
    tinCode: opt.tinCodeDefault,
    occupation: TRUST_OCCUPATION_LABEL,
    employer: '',
    cddExempt: true,
    cddExemptReasonCode: TRUST_CDD_EXEMPT_REASON,
    extra: {
      ...(priorExtra ?? {}),
      trustSubType: subType,
      acctCallClass: opt.acctCallClass,
      consumerFlag: opt.consumerFlag,
      cifConvention: opt.usesFakeCifConvention
        ? TRUST_BRANCH_PREFIX_CIF_CONVENTION
        : (priorExtra?.cifConvention ?? ''),
    },
  };
}
